import React from 'react'
import {
  GridPanelWrapper,
  useGridSelector,
  useGridApiContext,
  gridColumnVisibilityModelSelector,
} from '@mui/x-data-grid'
import { Box, FormControlLabel } from '@mui/material'
import { useGridRootProps } from '@mui/x-data-grid-premium'

type Weeks = string[]
type PanelProps = {
  weeks: Weeks
}

const commonFields = [
  {
    field: 'workCentreCode',
    headerName: 'Work Centre Code',
  },

  {
    field: 'runRate',
    headerName: 'Run rate',
  },
  {
    field: `minimumProductionOrderIncrement`,
    headerName: `MOI`,
  },
  {
    field: `minimumProductionOrderQuantity`,
    headerName: `MOQ`,
  },
  {
    field: `replenishmentTypeCode`,
    headerName: `Replenishment type`,
  },
  {
    field: `cycleTime`,
    headerName: `Cycle time`,
  },
  {
    field: `batchGroupCode`,
    headerName: `Batch group code`,
  },
  {
    field: `skuDescription`,
    headerName: `SKU description`,
  },
]
const commonFieldSet = new Set([
  'workCentreCode',
  'runRate',
  'minimumProductionOrderIncrement',
  'minimumProductionOrderQuantity',
  'replenishmentTypeCode',
  'cycleTime',
  'batchGroupCode',
  'skuDescription',
])

const uniqueFields = [
  { field: 'proposedProductionAmount', headerName: 'Production amount' },
  { field: 'adjustedProductionHours', headerName: 'Production time' },
  { field: 'templateWeek', headerName: 'Template week' },
  { field: 'weekCloseDaysProjected', headerName: 'Week close days projected' },
  { field: 'excessDaysProjected', headerName: 'Excess stock in days' },
  {
    field: 'alternativeWCProductionAmount',
    headerName: 'Alternative WC production amount',
  },
  {
    field: 'changeInProductionAmount',
    headerName: 'Change in production amount',
  },
  {
    field: 'changeInProductionHours',
    headerName: 'Change in production hours',
  },
  {
    field: 'cumulativeChangeInProduction',
    headerName: 'Cumulative change in production',
  },
  { field: 'initialProductionAmount', headerName: 'Initial production amount' },
  { field: 'initialProductionHours', headerName: 'Initial production hours' },
  {
    field: 'inventoryExcessProjected',
    headerName: 'Inventory excess projected',
  },
  { field: 'salesRate', headerName: 'Sales rate' },
  { field: 'siteWithSalesCount', headerName: 'Site with sales count' },
  { field: 'targetCloseDays', headerName: 'Target close days' },
  { field: 'targetCloseInventory', headerName: 'Target close inventory' },
  { field: 'templateViolation', headerName: 'Template violation' },
  { field: 'totalProductionAmount', headerName: 'Total production amount' },
  {
    field: 'weekCloseInventoryInitial',
    headerName: 'Week close inventory initial',
  },
  {
    field: 'weekCloseInventoryProjected',
    headerName: 'Week close inventory projected',
  },
].sort((a, b) => {
  return a.headerName.localeCompare(b.headerName)
})

const uniqueFieldsSet = new Set([
  'proposedProductionAmount',
  'adjustedProductionHours',
  'templateWeek',
  'weekCloseDaysProjected',
  'excessDaysProjected',
  'alternativeWCProductionAmount',
  'changeInProductionAmount',
  'changeInProductionHours',
  'cumulativeChangeInProduction',
  'initialProductionAmount',
  'initialProductionHours',
  'inventoryExcessProjected',
  'salesRate',
  'siteWithSalesCount',
  'targetCloseDays',
  'targetCloseInventory',
  'templateViolation',
  'totalProductionAmount',
  'weekCloseInventoryInitial',
  'weekCloseInventoryProjected',
])

const CustomColumnSelector = ({ weeks }: PanelProps) => {
  const apiRef = useGridApiContext()
  const rootProps = useGridRootProps()
  const columnVisibilityModel = useGridSelector(
    apiRef,
    gridColumnVisibilityModelSelector
  )

  const toggleColumn = (event: React.MouseEvent<HTMLButtonElement>) => {
    const { name } = event.target as HTMLInputElement
    if (commonFieldSet.has(name)) {
      apiRef.current.setColumnVisibility(
        name,
        columnVisibilityModel[name] === false
      )
      return
    }
    weeks.forEach((week) => {
      apiRef.current.setColumnVisibility(
        `${week}_${name}`,
        columnVisibilityModel[`${week}_${name}`] === false
      )
    })
  }
  console.log('columnVisibilityModel', columnVisibilityModel)
  const isChecked = (field: string) => {
    if (commonFieldSet.has(field)) {
      return columnVisibilityModel[field] !== false
    }
    return columnVisibilityModel[`${weeks[0]}_${field}`] !== false
  }
  return (
    <>
      {[...commonFields, ...uniqueFields].map((column) => (
        <FormControlLabel
          key={column.field}
          sx={{ marginLeft: '5px' }}
          control={
            <rootProps.slots.baseCheckbox
              checked={isChecked(column.field)}
              onClick={toggleColumn}
              name={column.field}
              sx={{ p: 0.5 }}
              {...rootProps.slotProps?.baseCheckbox}
            />
          }
          label={column.headerName || column.field}
        />
      ))}
    </>
  )
}
export const CustomColumnsPanel = ({ weeks }: PanelProps) => {
  return (
    <GridPanelWrapper>
      <Box
        display="flex"
        sx={{
          padding: '10px',
          width: '350px',
          flexDirection: 'column',
        }}
      >
        <CustomColumnSelector weeks={weeks} />
      </Box>
    </GridPanelWrapper>
  )
}
